<template>
  <v-container fluid>
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="8" class="d-flex align-center">
        <v-btn text color="primary" class="mr-3" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
        <div>
          <h2 class="text-h5 font-weight-bold">{{ pedido ? pedido.numero_display : 'Pedido de compra' }}</h2>
        </div>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-chip v-if="pedido" :color="estadoColor(pedido.estado)" dark class="mr-2">
          {{ estadoLabel(pedido.estado) }}
        </v-chip>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col v-for="i in 3" :key="i" cols="12">
        <v-skeleton-loader type="list-item-three-line" />
      </v-col>
    </v-row>

    <template v-else-if="pedido">
      <v-row>
        <!-- Info proveedor + acciones -->
        <v-col cols="12" md="4">
          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Proveedor</v-card-title>
            <v-card-text>
              <div class="font-weight-medium">{{ pedido.proveedor.nombre }}</div>
              <div class="caption grey--text mt-1" v-if="pedido.proveedor.telefono">
                <v-icon small class="mr-1">mdi-phone</v-icon>{{ pedido.proveedor.telefono }}
              </div>
              <div class="caption grey--text" v-if="pedido.proveedor.email">
                <v-icon small class="mr-1">mdi-email</v-icon>{{ pedido.proveedor.email }}
              </div>
              <div class="caption grey--text" v-if="pedido.proveedor.direccion">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>{{ pedido.proveedor.direccion }}
              </div>
            </v-card-text>
          </v-card>

          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">Datos</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Sucursal</span>
                <span class="caption">{{ pedido.sucursal_nombre }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Fecha</span>
                <span class="caption">{{ pedido.fecha_alta }}</span>
              </div>
              <div v-if="pedido.observaciones" class="mt-2">
                <span class="grey--text caption">Observaciones</span>
                <p class="body-2 mt-1">{{ pedido.observaciones }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Botones de acción -->
          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold">Acciones</v-card-title>
            <v-card-text class="d-flex flex-column" style="gap: 8px">
              <v-btn
                v-if="pedido.estado === 'borrador'"
                color="primary"
                outlined
                @click="irAEditar"
              >
                <v-icon left>mdi-pencil</v-icon>
                Editar pedido
              </v-btn>

              <v-btn
                v-if="pedido.estado === 'borrador'"
                color="error"
                outlined
                @click="dialogEliminar = true"
              >
                <v-icon left>mdi-delete</v-icon>
                Eliminar pedido
              </v-btn>

              <template v-for="transicion in pedido.transiciones_disponibles">
                <v-btn
                  v-if="transicion !== 'cancelado'"
                  :key="transicion"
                  :color="transicionColor(transicion)"
                  :loading="loadingTransicion === transicion"
                  @click="ejecutarTransicion(transicion)"
                >
                  <v-icon left>{{ transicionIcon(transicion) }}</v-icon>
                  {{ transicionLabel(transicion) }}
                </v-btn>
              </template>

              <v-btn
                v-if="pedido.transiciones_disponibles.includes('cancelado')"
                color="error"
                outlined
                @click="dialogCancelar = true"
              >
                <v-icon left>mdi-cancel</v-icon>
                Cancelar pedido
              </v-btn>

              <v-btn
                v-if="['enviado', 'recibido'].includes(pedido.estado)"
                color="indigo"
                outlined
                :loading="loadingPdf"
                @click="verPdf"
              >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Ver PDF
              </v-btn>

              <v-btn
                v-if="pedido.estado === 'recibido'"
                color="teal"
                outlined
                disabled
              >
                <v-icon left>mdi-file-document-plus</v-icon>
                Cargar factura de compra
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Detalles -->
        <v-col cols="12" md="8">
          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold">
              Detalle del pedido
              <v-spacer />
              <span class="caption grey--text">{{ pedido.detalles.length }} ítem(s)</span>
            </v-card-title>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th class="text-right">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="detalle in pedido.detalles" :key="detalle.id">
                    <td>{{ detalle.descripcion }}</td>
                    <td class="text-right">{{ detalle.cantidad }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Confirmar cancelar -->
    <dialog-confirmation-close
      v-if="dialogCancelar"
      @close-dialog="dialogCancelar = false"
      @process-accept="ejecutarTransicion('cancelado')"
    >
      <template v-slot:titulo>¿Cancelar pedido?</template>
      <template v-slot:message>Esta acción no se puede revertir. El pedido quedará cancelado.</template>
      <template v-slot:buttonOK>Cancelar pedido</template>
    </dialog-confirmation-close>

    <!-- Confirmar eliminar -->
    <dialog-confirmation-close
      v-if="dialogEliminar"
      @close-dialog="dialogEliminar = false"
      @process-accept="eliminar"
    >
      <template v-slot:titulo>¿Eliminar pedido?</template>
      <template v-slot:message>Se eliminará el pedido de forma permanente.</template>
      <template v-slot:buttonOK>Eliminar</template>
    </dialog-confirmation-close>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import DialogConfirmationClose from '@/components/dialogs/confirmation.close.vue';
import PedidoCompraAPI from '@/api/compra/pedido';

export default {
  name: 'CompraPedidoEmpresaConsultar',
  components: { DialogConfirmationClose },
  data() {
    return {
      pedido: null,
      loading: false,
      loadingTransicion: null,
      loadingPdf: false,
      dialogCancelar: false,
      dialogEliminar: false,
    };
  },
  computed: {
    pedidoId() {
      return this.$route.params.id || this.$route.params.uuid_pedido;
    },
    isSucursalContext() {
      return !!this.$route.params.uuid_sucursal;
    },
  },
  methods: {
    ...mapActions('compraPedido', ['get_pedido_compra', 'cambiar_estado_pedido_compra', 'destroy_pedido_compra']),

    estadoColor(estado) {
      const map = { borrador: 'grey', enviado: 'blue', recibido: 'green', cancelado: 'red darken-1' };
      return map[estado] || 'grey';
    },

    estadoLabel(estado) {
      const map = { borrador: 'Borrador', enviado: 'Enviado', recibido: 'Recibido', cancelado: 'Cancelado' };
      return map[estado] || estado;
    },

    transicionLabel(transicion) {
      const map = { enviado: 'Enviar al proveedor', recibido: 'Marcar como recibido', cancelado: 'Cancelar pedido' };
      return map[transicion] || transicion;
    },

    transicionIcon(transicion) {
      const map = { enviado: 'mdi-send', recibido: 'mdi-check-circle', cancelado: 'mdi-cancel' };
      return map[transicion] || 'mdi-arrow-right';
    },

    transicionColor(transicion) {
      const map = { enviado: 'primary', recibido: 'success', cancelado: 'error' };
      return map[transicion] || 'grey';
    },

    async fetchPedido() {
      this.loading = true;
      try {
        const res = await this.get_pedido_compra(this.pedidoId);
        this.pedido = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async ejecutarTransicion(estado) {
      this.dialogCancelar = false;
      this.loadingTransicion = estado;
      try {
        await this.cambiar_estado_pedido_compra({ id: this.pedidoId, estado });
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: 'Estado actualizado', icon: 'mdi-check', timeout: 2000,
        });
        await this.fetchPedido();
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al cambiar el estado';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: msg, icon: 'mdi-alert', timeout: 3500,
        });
      } finally {
        this.loadingTransicion = null;
      }
    },

    async eliminar() {
      this.dialogEliminar = false;
      try {
        await this.destroy_pedido_compra(this.pedidoId);
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: 'Pedido eliminado', icon: 'mdi-check', timeout: 2000,
        });
        this.$router.back();
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al eliminar';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: msg, icon: 'mdi-alert', timeout: 3500,
        });
      }
    },

    async verPdf() {
      this.loadingPdf = true;
      try {
        const res = await PedidoCompraAPI.pdf(this.pedidoId);
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
      } catch (e) {
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: 'Error al obtener el PDF', icon: 'mdi-alert', timeout: 3000,
        });
      } finally {
        this.loadingPdf = false;
      }
    },

    irAEditar() {
      if (this.isSucursalContext) {
        this.$router.push({
          name: 'empresa.sucursal.compras.pedido.editar',
          params: { uuid_sucursal: this.$route.params.uuid_sucursal, id: this.pedidoId },
        });
      } else {
        this.$router.push({ name: 'empresa.comprasPedidoEditar', params: { id: this.pedidoId } });
      }
    },
  },
  created() {
    this.fetchPedido();
  },
};
</script>
